import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';

// Mirrors payroll.service.ts's blob-error idiom (I5): axios responseType:'blob' applies to error
// responses too, so a 4xx backend body (plain text or JSON) arrives as a Blob rather than a
// string. Read it back to text (unwrapping a `{message}` JSON body when present) and rewrite
// error.response.data in place before rethrowing, so ToastContext's showApiError shows the real
// message instead of falling back to a generic one.
function blobToText(blob: Blob): Promise<string> {
    if (typeof (blob as any).text === 'function') {
        return (blob as any).text();
    }
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
    });
}

async function rethrowBlobError(error: any): Promise<never> {
    const data = error?.response?.data;
    if (data instanceof Blob) {
        try {
            const text = await blobToText(data);
            let message = text;
            try {
                const parsed = JSON.parse(text);
                if (parsed && typeof parsed === 'object' && typeof parsed.message === 'string') {
                    message = parsed.message;
                }
            } catch {
                // Not JSON — use the raw text body as-is.
            }
            if (message) {
                error.response.data = message;
            }
        } catch {
            // Blob unreadable (e.g. already consumed); fall through with the generic message.
        }
    }
    throw error;
}

// Fix-round M-1: mirrors the backend's SafeFilenames.safeFileNameSegment (same whitelist, same
// 40-char cap, same UUID fallback) so the downloaded filename matches what the server actually
// named the attachment, instead of the hardcoded `<kind>-${userId}.pdf` used previously. Read via
// Content-Disposition was considered and rejected: CorsConfig does not set exposedHeaders, so
// axios/browser JS cannot see that header on this app's responses — verified by reading
// backend/src/main/java/com/kervolt/oms/config/CorsConfig.java, which configures allowedOrigins/
// allowedMethods/allowedHeaders/allowCredentials but never calls setExposedHeaders. Deriving from
// employeeCode client-side (with userId fallback) is therefore the smallest correct option.
const UNSAFE_FILENAME_CHARS = /[^A-Za-z0-9._-]/g;
const MAX_FILENAME_SEGMENT_LENGTH = 40;

function safeFileNameSegment(rawSegment: string | null | undefined, fallbackId: string): string {
    if (!rawSegment || !rawSegment.trim()) {
        return fallbackId;
    }
    let sanitized = rawSegment.replace(UNSAFE_FILENAME_CHARS, '_');
    if (sanitized.length > MAX_FILENAME_SEGMENT_LENGTH) {
        sanitized = sanitized.substring(0, MAX_FILENAME_SEGMENT_LENGTH);
    }
    return sanitized.trim() ? sanitized : fallbackId;
}

function downloadBlob(data: BlobPart, fileName: string): void {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

async function downloadPdf(url: string, fileName: string): Promise<void> {
    try {
        const response = await axios.get(url, { headers: AuthService.getAuthHeader(), responseType: 'blob' });
        downloadBlob(response.data, fileName);
    } catch (error) {
        return rethrowBlobError(error);
    }
}

export const EmployeeLetterService = {
    downloadAppointmentLetter: async (userId: string, employeeCode?: string | null): Promise<void> => {
        const institutionId = AuthService.getSelectedInstitution()!;
        try {
            const response = await axios.get(
                ENDPOINTS.EMPLOYEE_LETTER.APPOINTMENT_LETTER(institutionId, userId),
                { headers: AuthService.getAuthHeader(), responseType: 'blob' }
            );
            downloadBlob(response.data, `appointment-letter-${safeFileNameSegment(employeeCode, userId)}.pdf`);
        } catch (error) {
            return rethrowBlobError(error);
        }
    },

    downloadIdCard: async (userId: string, employeeCode?: string | null): Promise<void> => {
        const institutionId = AuthService.getSelectedInstitution()!;
        try {
            const response = await axios.get(
                ENDPOINTS.EMPLOYEE_LETTER.ID_CARD(institutionId, userId),
                { headers: AuthService.getAuthHeader(), responseType: 'blob' }
            );
            downloadBlob(response.data, `id-card-${safeFileNameSegment(employeeCode, userId)}.pdf`);
        } catch (error) {
            return rethrowBlobError(error);
        }
    },

    downloadExperienceLetter: async (userId: string, employeeCode?: string | null): Promise<void> => {
        const institutionId = AuthService.getSelectedInstitution()!;
        return downloadPdf(
            ENDPOINTS.EMPLOYEE_LETTER.EXPERIENCE_LETTER(institutionId, userId),
            `experience-letter-${safeFileNameSegment(employeeCode, userId)}.pdf`
        );
    },

    downloadSalaryCertificate: async (userId: string, employeeCode?: string | null): Promise<void> => {
        const institutionId = AuthService.getSelectedInstitution()!;
        return downloadPdf(
            ENDPOINTS.EMPLOYEE_LETTER.SALARY_CERTIFICATE(institutionId, userId),
            `salary-certificate-${safeFileNameSegment(employeeCode, userId)}.pdf`
        );
    },

    // The settlement carries a username, not an employee code, so the name segment is the username.
    downloadFinalSettlement: async (settlementId: string, nameSegment?: string | null): Promise<void> => {
        const institutionId = AuthService.getSelectedInstitution()!;
        return downloadPdf(
            ENDPOINTS.EMPLOYEE_LETTER.FINAL_SETTLEMENT(institutionId, settlementId),
            `final-settlement-${safeFileNameSegment(nameSegment, settlementId)}.pdf`
        );
    },

    downloadMaternityStatement: async (leaveApplicationId: string, employeeCode?: string | null): Promise<void> => {
        const institutionId = AuthService.getSelectedInstitution()!;
        return downloadPdf(
            ENDPOINTS.EMPLOYEE_LETTER.MATERNITY_STATEMENT(institutionId, leaveApplicationId),
            `maternity-statement-${safeFileNameSegment(employeeCode, leaveApplicationId)}.pdf`
        );
    },
};
